import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type Task } from '../../../services/projectManagement/tasksService';
import type { Milestone } from '../../../services/projectManagement/milestonesService';
import type { PmResizeEdge, PmScheduleViolation } from '../lib/pmScheduling';

interface GanttChartViewProps {
  tasks: Task[];
  milestones?: Milestone[];
  onTaskClick: (taskId: string) => void;
  onMilestoneClick?: (milestone: Milestone) => void;
  criticalTaskIds?: string[];
  violationTaskIds?: string[];
  violations?: PmScheduleViolation[];
  slackByTaskId?: Record<string, number>;
  onTaskShiftDays?: (taskId: string, deltaDays: number) => void | Promise<void>;
  onTaskResizeDays?: (taskId: string, edge: PmResizeEdge, deltaDays: number) => void | Promise<void>;
}

const GanttChartView: React.FC<GanttChartViewProps> = ({
  tasks,
  milestones,
  onTaskClick,
  onMilestoneClick,
  criticalTaskIds,
  violationTaskIds,
  violations,
  slackByTaskId,
  onTaskShiftDays,
  onTaskResizeDays,
}) => {
  const [viewMode, setViewMode] = useState<'week' | 'month' | 'quarter'>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const timelineRef = useRef<HTMLDivElement | null>(null);

  const [drag, setDrag] = useState<{
    taskId: string;
    startX: number;
    latestX: number;
    moved: boolean;
    mode: 'move' | 'resize';
    edge?: PmResizeEdge;
  } | null>(null);

  const dragRef = useRef<typeof drag>(null);

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  const criticalSet = useMemo(() => new Set(criticalTaskIds || []), [criticalTaskIds]);
  const violationSet = useMemo(() => new Set(violationTaskIds || []), [violationTaskIds]);

  // Filter tasks that have dates
  const tasksWithDates = useMemo(() => {
    return tasks.filter(task => task.start_date || task.due_date);
  }, [tasks]);

  // Calculate date range based on view mode
  const { startDate, endDate, columns } = useMemo(() => {
    const today = new Date(currentDate);
    let start: Date;
    let end: Date;
    let cols: Date[] = [];

    if (viewMode === 'week') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
      end = new Date(start);
      end.setDate(end.getDate() + 28); // 4 weeks
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        cols.push(new Date(d));
      }
    } else if (viewMode === 'month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        cols.push(new Date(d));
      }
    } else { // quarter
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
      
      // Weekly columns for quarter view
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 7)) {
        cols.push(new Date(d));
      }
    }

    return { startDate: start, endDate: end, columns: cols };
  }, [currentDate, viewMode]);

  const milestoneMarkers = useMemo(() => {
    const list = Array.isArray(milestones) ? milestones : [];
    if (list.length === 0) return [] as Array<{ milestone: Milestone; leftPercent: number }>;

    const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    if (!Number.isFinite(totalDays) || totalDays <= 0) {
      return [] as Array<{ milestone: Milestone; leftPercent: number }>;
    }

    return list
      .map((m) => {
        const due = new Date(m.due_date);
        if (!Number.isFinite(due.getTime())) return null;
        if (due < startDate || due > endDate) return null;

        const offsetDays = (due.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
        const leftPercent = (offsetDays / totalDays) * 100;
        if (!Number.isFinite(leftPercent)) return null;

        return { milestone: m, leftPercent };
      })
      .filter(Boolean)
      .sort((a, b) => (a!.leftPercent || 0) - (b!.leftPercent || 0)) as Array<{ milestone: Milestone; leftPercent: number }>;
  }, [endDate, milestones, startDate]);

  useEffect(() => {
    const onMove = (e: PointerEvent | MouseEvent) => {
      const current = dragRef.current as any;
      if (!current) return;
      const clientX = (e as any).clientX;
      const moved = current.moved || Math.abs(clientX - current.startX) >= 5;
      dragRef.current = { ...current, latestX: clientX, moved };
      setDrag(dragRef.current);
    };

    const onUp = async () => {
      const current = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!current) return;
      if (!current.moved) return;

      if (current.mode === 'resize') {
        if (!onTaskResizeDays || !current.edge) return;
      } else {
        if (!onTaskShiftDays) return;
      }

      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = rect.width || 0;
      if (width <= 0) return;

      const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      if (!Number.isFinite(totalDays) || totalDays <= 0) return;

      const unitDays = viewMode === 'quarter' ? 7 : 1;
      const pixelsPerUnit = width / (totalDays / unitDays);
      if (!Number.isFinite(pixelsPerUnit) || pixelsPerUnit <= 0) return;

      const deltaUnits = Math.round((current.latestX - current.startX) / pixelsPerUnit);
      const deltaDays = deltaUnits * unitDays;
      if (deltaDays === 0) return;

      if (current.mode === 'resize') {
        if (!onTaskResizeDays || !current.edge) return;
        await onTaskResizeDays(current.taskId, current.edge, deltaDays);
      } else {
        if (!onTaskShiftDays) return;
        await onTaskShiftDays(current.taskId, deltaDays);
      }
    };

    window.addEventListener('pointermove', onMove as any);
    window.addEventListener('mousemove', onMove as any);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove as any);
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('mouseup', onUp);
    };
  }, [endDate, onTaskResizeDays, onTaskShiftDays, startDate, viewMode]);

  // Calculate task bar position and width
  const getTaskBarStyle = (task: Task) => {
    const taskStart = task.start_date ? new Date(task.start_date) : new Date(task.due_date!);
    const taskEnd = task.due_date ? new Date(task.due_date) : new Date(task.start_date!);
    
    const totalDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    const startOffset = Math.max(0, (taskStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (taskEnd.getTime() - taskStart.getTime()) / (1000 * 60 * 60 * 24));
    
    const leftPercent = (startOffset / totalDays) * 100;
    const widthPercent = (duration / totalDays) * 100;
    
    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`,
    };
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-600';
      case 'in-progress': return 'bg-blue-600';
      case 'review': return 'bg-purple-600';
      case 'blocked': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatColumnHeader = (date: Date) => {
    if (viewMode === 'week') {
      return date.getDate().toString();
    } else if (viewMode === 'month') {
      if (date.getDate() === 1 || date.getDate() % 5 === 0) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return '';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 28 : -28));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 3 : -3));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 3 : -3));
    }
    setCurrentDate(newDate);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigateDate('prev')}
            className="rounded-lg px-3 py-2 hover:bg-gray-100"
          >
            ← Previous
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
          >
            Today
          </button>
          <button
            onClick={() => navigateDate('next')}
            className="rounded-lg px-3 py-2 hover:bg-gray-100"
          >
            Next →
          </button>
        </div>

        <h2 className="text-lg font-bold text-gray-900">
          {startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - {endDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h2>

        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1 text-sm font-medium ${
              viewMode === 'week'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1 text-sm font-medium ${
              viewMode === 'month'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setViewMode('quarter')}
            className={`px-3 py-1 text-sm font-medium ${
              viewMode === 'quarter'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Quarter
          </button>
        </div>
      </div>

      {/* Gantt Chart */}
      {tasksWithDates.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">📅</div>
          <h3 className="text-lg font-semibold text-gray-900">No Scheduled Tasks</h3>
          <p className="mt-2 text-sm text-gray-600">
            Add start dates and due dates to your tasks to see them on the timeline
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white overflow-x-auto">
          <div className="min-w-[800px]">
            {Array.isArray(violations) && violations.length > 0 && (
              <div className="border-b border-gray-200 bg-red-50 px-4 py-3">
                <div className="text-sm font-semibold text-red-800">{violations.length} scheduling issues</div>
                <div className="mt-1 space-y-1">
                  {violations.slice(0, 3).map((v, idx) => (
                    <div key={idx} className="text-xs text-red-700">{v.message}</div>
                  ))}
                  {violations.length > 3 && (
                    <div className="text-xs text-red-700">+{violations.length - 3} more</div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Header */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <div className="w-64 flex-shrink-0 border-r border-gray-200 px-4 py-3">
                <span className="text-sm font-semibold text-gray-700">Task</span>
              </div>
              <div ref={timelineRef} className="flex-1 relative">
                <div className="flex">
                  {columns.map((date, index) => (
                    <div
                      key={index}
                      className={`flex-1 border-r border-gray-200 px-2 py-3 text-center text-xs ${
                        isToday(date) ? 'bg-blue-50 font-semibold text-blue-600' : 'text-gray-600'
                      }`}
                    >
                      {formatColumnHeader(date)}
                    </div>
                  ))}
                </div>

                {milestoneMarkers.length > 0 && (
                  <div className="absolute inset-0 pointer-events-none">
                    {milestoneMarkers.map(({ milestone, leftPercent }) => (
                      <button
                        key={milestone.id}
                        type="button"
                        aria-label={`Milestone: ${milestone.name}`}
                        title={`${milestone.name} • ${new Date(milestone.due_date).toLocaleDateString()}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMilestoneClick?.(milestone);
                        }}
                        className="pointer-events-auto absolute top-1 -translate-x-1/2"
                        style={{ left: `${leftPercent}%` }}
                      >
                        <div
                          className={`h-3 w-3 rotate-45 border ${milestone.completed ? 'border-gray-400' : 'border-gray-700'}`}
                          style={{ backgroundColor: milestone.color || '#3b82f6' }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Task Rows */}
            <div className="divide-y divide-gray-200">
              {tasksWithDates.map((task) => {
                const barStyle = getTaskBarStyle(task);
                const isCritical = criticalSet.has(task.id);
                const isViolation = violationSet.has(task.id);
                const slackDays = typeof slackByTaskId?.[task.id] === 'number' ? slackByTaskId?.[task.id] : undefined;
                
                return (
                  <div key={task.id} className="flex hover:bg-gray-50">
                    {/* Task Info Column */}
                    <div className="w-64 flex-shrink-0 border-r border-gray-200 px-4 py-3">
                      <button
                        onClick={() => onTaskClick(task.id)}
                        className="text-left w-full"
                      >
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${getPriorityColor(task.priority)}`} />
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {task.title}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {task.project_name}
                        </div>
                        {typeof slackDays === 'number' && (
                          <div className="mt-1 text-[11px] text-gray-500">
                            Slack: {slackDays}d
                          </div>
                        )}
                      </button>
                    </div>

                    {/* Timeline Column */}
                    <div className="flex-1 relative py-3 px-2">
                      <div
                        className={`absolute h-8 rounded transition-opacity hover:opacity-80 ${
                          isViolation ? 'ring-2 ring-red-500' : isCritical ? 'ring-2 ring-yellow-400' : ''
                        }`}
                        style={{
                          ...barStyle,
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <button
                          type="button"
                          aria-label={`Resize start of ${task.title}`}
                          onMouseDown={(e) => {
                            if (!onTaskResizeDays) return;
                            e.stopPropagation();
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'resize' as const, edge: 'start' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          onPointerDown={(e) => {
                            if (!onTaskResizeDays) return;
                            e.stopPropagation();
                            if (typeof (e as any).pointerId === 'number') {
                              (e.currentTarget as HTMLButtonElement).setPointerCapture?.((e as any).pointerId);
                            }
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'resize' as const, edge: 'start' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          className="absolute left-0 top-0 h-full w-2 cursor-ew-resize rounded-l bg-black/20"
                        />

                        <button
                          type="button"
                          onClick={() => {
                            if (drag?.taskId === task.id && drag.moved) return;
                            onTaskClick(task.id);
                          }}
                          onMouseDown={(e) => {
                            if (!onTaskShiftDays) return;
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'move' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          onPointerDown={(e) => {
                            if (!onTaskShiftDays) return;
                            if (typeof (e as any).pointerId === 'number') {
                              (e.currentTarget as HTMLButtonElement).setPointerCapture?.((e as any).pointerId);
                            }
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'move' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          className="h-full w-full cursor-grab"
                          title={`${task.title}\n${task.start_date} - ${task.due_date}`}
                        >
                          <div className={`h-full rounded ${getStatusColor(task.status)} flex items-center px-2`}>
                            <span className="text-xs text-white font-medium truncate">
                              {task.title}
                            </span>
                          </div>
                        </button>

                        <button
                          type="button"
                          aria-label={`Resize end of ${task.title}`}
                          onMouseDown={(e) => {
                            if (!onTaskResizeDays) return;
                            e.stopPropagation();
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'resize' as const, edge: 'end' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          onPointerDown={(e) => {
                            if (!onTaskResizeDays) return;
                            e.stopPropagation();
                            if (typeof (e as any).pointerId === 'number') {
                              (e.currentTarget as HTMLButtonElement).setPointerCapture?.((e as any).pointerId);
                            }
                            const next = { taskId: task.id, startX: e.clientX, latestX: e.clientX, moved: false, mode: 'resize' as const, edge: 'end' as const };
                            dragRef.current = next;
                            setDrag(next);
                          }}
                          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize rounded-r bg-black/20"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        <span className="text-gray-700 font-medium">Status:</span>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-gray-600" />
          <span className="text-gray-700">To Do</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-blue-600" />
          <span className="text-gray-700">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-purple-600" />
          <span className="text-gray-700">Review</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-green-600" />
          <span className="text-gray-700">Done</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-red-600" />
          <span className="text-gray-700">Blocked</span>
        </div>
        <span className="text-gray-300">|</span>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-yellow-400" />
          <span className="text-gray-700">Critical path</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded bg-red-500" />
          <span className="text-gray-700">Constraint issue</span>
        </div>
      </div>
    </div>
  );
};

export default GanttChartView;
